// INFO BANNER v7
//
// AUTH : access_token JWT (AsyncStorage 'harmonia_session')
//
// FONCTIONNALITÉS :
//   - Fetch toutes les infos via get-infos
//   - Cycling automatique : fin de défilement → info suivante
//   - Tap bandeau → pause + carte de lecture expansée (slide-down)
//   - Tap carte / bouton "Fermer et reprendre" → reprise
//     exactement au point de pause, même vitesse (18 ms/px)
//   - Navigation prev / next dans la carte
//   - Fallback sur les props title / text si le fetch échoue
//   - v3 : bouton "agrandir" (expand-outline) ouvre un plein écran avec
//     le contenu complet, navigation prev/next, et un bouton "réduire".
//   - v4 : FIX — Modal de React Native ne se rend pas de façon fiable sur
//     react-native-web (bug silencieux). Sur web, remplacé par une View
//     en position 'fixed'. Sur mobile, Modal natif conservé.
//   - v5 : FIX — l'icône expand du bandeau replié était purement
//     décorative (aucun onPress propre) ; c'est maintenant un vrai
//     bouton qui appelle openFullscreen() directement.
//   - v6 : FIX — fsOverlayWeb portait `flex: 1` hérité, sans effet sur un
//     élément en position 'fixed' (qui sort du flux du document) ; le
//     style est maintenant autonome avec son propre display/justify/align.
//   - v7 : FIX CRITIQUE — même avec position:'fixed' + zIndex élevé,
//     l'overlay restait piégé DERRIÈRE le contenu de l'écran parent.
//     Cause : sur le web, `position: fixed` ne garantit PAS de sortir des
//     stacking contexts créés par des ancêtres ayant eux-mêmes un
//     z-index explicite (ici, infoBannerOverlay dans games-screen.tsx a
//     zIndex: 50) — l'overlay plein écran restait donc empilé À
//     L'INTÉRIEUR de ce contexte local au lieu de flotter par-dessus
//     tout le document. Solution définitive : sur web, l'overlay est
//     rendu via createPortal directement dans document.body, ce qui le
//     sort complètement de la hiérarchie React (et donc CSS) de
//     games-screen.tsx — aucun ancêtre, aucun stacking context hérité,
//     garantit un affichage au-dessus de tout, toujours. Sur mobile,
//     Modal natif reste utilisé tel quel (déjà fiable, aucun changement).
// =====================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Animated,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons }  from '@expo/vector-icons';
import AsyncStorage  from '@react-native-async-storage/async-storage';
import { useTranslation } from 'react-i18next';

// createPortal n'est disponible/importable que sur web (react-dom).
// L'import est conditionnel pour ne jamais casser le bundle mobile, qui
// n'a pas ce module et n'en a pas besoin (Modal natif suffit là-bas).
let createPortal: ((children: React.ReactNode, container: Element) => React.ReactPortal) | null = null;
if (Platform.OS === 'web') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  createPortal = require('react-dom').createPortal;
}

// ─── Config ───────────────────────────────────────────────────────────────────
const WS_BASE  = 'https://eueke282zksk1zki18susjdksisk18sj.onrender.com';
const INFO_URL = `${WS_BASE}/info`;

// ─── Types ────────────────────────────────────────────────────────────────────
interface InfoItem {
  id:         string;
  titre:      string;
  texte:      string;
  created_at: string;
}

interface InfoBannerProps {
  visible: boolean;
  /** Valeur de secours si le fetch échoue */
  title?: string;
  text?:  string;
}

// ─── Composant ────────────────────────────────────────────────────────────────
export default function InfoBanner({
  visible,
  title: fallbackTitle = '',
  text:  fallbackText  = '',
}: InfoBannerProps) {
  const { t } = useTranslation();

  const [infos, setInfos]               = useState<InfoItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [ready, setReady]               = useState(false);
  const [expanded, setExpanded]         = useState(false);

  // NOUVEAU v3 : visibilité de la Modal plein écran, déclenchée par le
  // bouton "agrandir" (expand-outline) dans la carte dépliée.
  const [fullscreenVisible, setFullscreenVisible] = useState(false);

  // ── Animations ─────────────────────────────────────────────────────────────
  const slideAnim  = useRef(new Animated.Value(-96)).current;
  const scrollAnim = useRef(new Animated.Value(0)).current;
  const cardAnim   = useRef(new Animated.Value(0)).current;

  // ── Mesures layout ─────────────────────────────────────────────────────────
  const containerW        = useRef(260);
  const textW             = useRef(800);
  const measuredContainer = useRef(false);
  const measuredText      = useRef(false);

  // ── État défilement ────────────────────────────────────────────────────────
  const animRef         = useRef<Animated.CompositeAnimation | null>(null);
  const pausedAt        = useRef(0);
  const isPaused        = useRef(false);

  // Refs miroir pour accès dans les callbacks sans créer de dépendances
  const infosRef        = useRef<InfoItem[]>([]);
  const currentIndexRef = useRef(0);
  const visibleRef      = useRef(visible);
  const readyRef        = useRef(ready);

  useEffect(() => { infosRef.current        = infos;        }, [infos]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { visibleRef.current      = visible;      }, [visible]);
  useEffect(() => { readyRef.current        = ready;        }, [ready]);

  // ── Fetch toutes les infos ─────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const raw = await AsyncStorage.getItem('harmonia_session');
        if (!raw) {
          if (fallbackTitle || fallbackText)
            setInfos([{ id: 'fallback', titre: fallbackTitle, texte: fallbackText, created_at: '' }]);
          setReady(true);
          return;
        }

        const { access_token } = JSON.parse(raw);
        const res  = await fetch(INFO_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ action: 'get-infos', access_token }),
        });
        const data = await res.json();

        if (data.success && data.infos?.length > 0) {
          setInfos(data.infos);
        } else if (fallbackTitle || fallbackText) {
          setInfos([{ id: 'fallback', titre: fallbackTitle, texte: fallbackText, created_at: '' }]);
        }
      } catch {
        // Conserve le fallback silencieusement
        if (fallbackTitle || fallbackText)
          setInfos([{ id: 'fallback', titre: fallbackTitle, texte: fallbackText, created_at: '' }]);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  // ── startScrollFrom ────────────────────────────────────────────────────────
  //
  // Lance le défilement depuis une position X arbitraire.
  // La durée est proportionnelle à la distance restante → vitesse constante.
  // Le callback onFinish avance l'index pour cycler automatiquement.
  // ──────────────────────────────────────────────────────────────────────────
  const startScrollFrom = useCallback((fromX: number) => {
    if (!measuredContainer.current || !measuredText.current) return;

    animRef.current?.stop();
    scrollAnim.setValue(fromX);

    const distance = fromX - (-textW.current);
    if (distance <= 0) return;

    const anim = Animated.timing(scrollAnim, {
      toValue:         -textW.current,
      duration:        distance * 18,   // 18 ms/px → vitesse identique à v1
      useNativeDriver: true,
    });

    animRef.current = anim;
    anim.start(({ finished }) => {
      if (!finished || isPaused.current) return;

      // Passage à l'info suivante
      const next = (currentIndexRef.current + 1) % infosRef.current.length;
      currentIndexRef.current = next;
      measuredText.current    = false; // attendre onLayout du nouveau texte
      setCurrentIndex(next);
    });
  }, [scrollAnim]);

  // ── Slide-down du bandeau selon visible ───────────────────────────────────
  useEffect(() => {
    Animated.spring(slideAnim, {
      toValue:         visible ? 0 : -96,
      useNativeDriver: true,
      speed:           18,
      bounciness:      2,
    }).start();

    if (!visible) {
      animRef.current?.stop();
      isPaused.current = false;
      setExpanded(false);
      setFullscreenVisible(false);
    }
  }, [visible]);

  // ── Tap : pause ↔ reprise ─────────────────────────────────────────────────
  const handleTap = useCallback(() => {
    if (expanded) {
      // Fermeture carte → reprise au point de pause exact
      Animated.timing(cardAnim, {
        toValue: 0, duration: 180, useNativeDriver: true,
      }).start(() => setExpanded(false));
      isPaused.current = false;
      startScrollFrom(pausedAt.current);
    } else {
      // Pause → ouverture carte
      animRef.current?.stop();
      scrollAnim.stopAnimation(val => { pausedAt.current = val; });
      isPaused.current = true;
      setExpanded(true);
      cardAnim.setValue(0);
      Animated.timing(cardAnim, {
        toValue: 1, duration: 250, useNativeDriver: true,
      }).start();
    }
  }, [expanded, startScrollFrom, scrollAnim, cardAnim]);

  // ── Navigation prev / next (partagée carte ET modal plein écran) ─────────
  const navigateTo = useCallback((idx: number) => {
    currentIndexRef.current = idx;
    measuredText.current    = false;
    pausedAt.current        = containerW.current; // reprend depuis la droite
    setCurrentIndex(idx);
  }, []);

  // ── NOUVEAU v3 : ouverture / fermeture de la Modal plein écran ───────────
  // Ouvrir la modal : on s'assure que le ticker reste en pause (comme pour
  // la carte dépliée classique) pendant que le plein écran est affiché,
  // pour que la lecture ne défile pas "derrière" sans qu'on la voie.
  const openFullscreen = useCallback(() => {
    if (!isPaused.current) {
      animRef.current?.stop();
      scrollAnim.stopAnimation(val => { pausedAt.current = val; });
      isPaused.current = true;
    }
    setFullscreenVisible(true);
  }, [scrollAnim]);

  // Fermer la modal ("réduire") : reprend automatiquement la lecture du
  // ticker exactement là où elle en était, comme le bouton "Fermer et
  // reprendre" existant sur la carte classique — cohérence de comportement
  // entre les deux modes d'affichage.
  const closeFullscreen = useCallback(() => {
    setFullscreenVisible(false);
    isPaused.current = false;
    startScrollFrom(pausedAt.current);
  }, [startScrollFrom]);

  // ── Rien à afficher ───────────────────────────────────────────────────────
  if (!ready || infos.length === 0) return null;

  const current    = infos[currentIndex] ?? infos[0];
  const tickerText = `${current.texte}     ✦     `;

  return (
    <View style={styles.outerContainer}>

      {/* ══════════════════════ BANDEAU ══════════════════════ */}
      <Animated.View
        style={[
          styles.wrapper,
          { transform: [{ translateY: slideAnim }] },
          !visible && styles.hiddenPointer,
        ]}
        pointerEvents={visible ? 'auto' : 'none'}
      >
        <TouchableOpacity onPress={handleTap} activeOpacity={0.88}>
          <View style={styles.inner}>

            {/* Pill titre */}
            <View style={styles.titlePill}>
              <Ionicons name="megaphone-outline" size={11} color="#FFD700" />
              <Text style={styles.titleText} numberOfLines={1}>
                {current.titre.length > 18
                  ? `${current.titre.slice(0, 18)}…`
                  : current.titre}
              </Text>
            </View>

            {/* Séparateur vertical */}
            <View style={styles.sep} />

            {/* Zone ticker */}
            <View
              style={styles.tickerZone}
              onLayout={e => {
                const w = e.nativeEvent.layout.width;
                if (w <= 0) return;
                containerW.current        = w;
                measuredContainer.current = true;
                if (visibleRef.current && readyRef.current && measuredText.current && !isPaused.current)
                  startScrollFrom(containerW.current);
              }}
            >
              {/*
                key={currentIndex} → force le remontage du View à chaque
                changement d'info, ce qui garantit un onLayout fresh
                même si les deux textes ont une largeur identique.
              */}
              <Animated.View
                key={`ticker-${currentIndex}`}
                style={[
                  styles.tickerContent,
                  { transform: [{ translateX: scrollAnim }] },
                ]}
                onLayout={e => {
                  const w = e.nativeEvent.layout.width;
                  if (w <= 0) return;
                  textW.current        = w;
                  measuredText.current = true;
                  if (visibleRef.current && readyRef.current && measuredContainer.current && !isPaused.current)
                    startScrollFrom(containerW.current);
                }}
              >
                <Text
                  style={[
                    styles.tickerText,
                    Platform.OS === 'web' ? ({ whiteSpace: 'nowrap' } as any) : null,
                  ]}
                >
                  {tickerText}
                </Text>
              </Animated.View>
            </View>

            {/*
              FIX v5 : cette icône était PUREMENT décorative jusqu'ici —
              elle vivait à l'intérieur du même TouchableOpacity que tout
              le bandeau (celui qui appelle handleTap pour ouvrir/fermer
              la carte dépliée), sans jamais avoir son propre onPress. Un
              tap dessus ouvrait donc seulement la carte dépliée classique
              (comme taper n'importe où ailleurs sur le bandeau) — jamais
              le plein écran, quoi qu'il y paraisse visuellement. C'est
              exactement le bug remonté : cliquer sur cette icône dans le
              bandeau replié ne faisait "rien" d'observable de nouveau.
              Elle devient maintenant un vrai bouton indépendant
              (TouchableOpacity propre, stopPropagation implicite car pas
              imbriqué dans un autre Pressable actif au même niveau) qui
              appelle directement openFullscreen() — un seul tap depuis le
              bandeau replié suffit désormais à afficher le plein écran,
              sans avoir à ouvrir la carte intermédiaire au préalable.
            */}
            <TouchableOpacity
              onPress={(e) => {
                // stopPropagation explicite : sécurise contre toute
                // remontée d'événement vers le TouchableOpacity parent
                // (handleTap) sur des configurations où la gestion de
                // gestes imbriqués diffère (notamment certaines versions
                // de react-native-web) — sans ça, un tap ici pourrait
                // déclencher les deux handlers à la suite (ouvrir la
                // carte ET le plein écran en même temps).
                e.stopPropagation?.();
                openFullscreen();
              }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              style={{ flexShrink: 0 }}
            >
              <Ionicons
                name="expand-outline"
                size={13}
                color="rgba(255,215,0,0.55)"
              />
            </TouchableOpacity>

          </View>
        </TouchableOpacity>
      </Animated.View>

      {/* ══════════════════════ CARTE ══════════════════════ */}
      {expanded && (
        <Animated.View
          style={[
            styles.card,
            {
              opacity:   cardAnim,
              transform: [{
                translateY: cardAnim.interpolate({
                  inputRange:  [0, 1],
                  outputRange: [-10, 0],
                }),
              }],
            },
          ]}
        >
          {/* Header */}
          <View style={styles.cardHeader}>
            <View style={styles.cardTitleRow}>
              <Ionicons name="megaphone-outline" size={14} color="#FFD700" />
              <Text style={styles.cardTitle}>{current.titre}</Text>
            </View>
            <View style={styles.cardHeaderRight}>
              {infos.length > 1 && (
                <Text style={styles.cardCounter}>
                  {currentIndex + 1} / {infos.length}
                </Text>
              )}
              {/* NOUVEAU v3 : bouton agrandir → Modal plein écran */}
              <TouchableOpacity
                onPress={openFullscreen}
                style={styles.expandBtn}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="expand-outline" size={15} color="#FFD700" />
              </TouchableOpacity>
            </View>
          </View>

          {/* Texte complet */}
          <Text style={styles.cardBody}>{current.texte}</Text>

          {/* Navigation prev / next */}
          {infos.length > 1 && (
            <View style={styles.cardNav}>
              <TouchableOpacity
                style={styles.navBtn}
                onPress={() => navigateTo((currentIndex - 1 + infos.length) % infos.length)}
              >
                <Ionicons name="chevron-back" size={14} color="rgba(255,255,255,0.65)" />
                <Text style={styles.navText}>{t('infoBanner.previous')}</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.navBtn}
                onPress={() => navigateTo((currentIndex + 1) % infos.length)}
              >
                <Text style={styles.navText}>{t('infoBanner.next')}</Text>
                <Ionicons name="chevron-forward" size={14} color="rgba(255,255,255,0.65)" />
              </TouchableOpacity>
            </View>
          )}

          {/* Bouton fermer */}
          <TouchableOpacity style={styles.closeBtn} onPress={handleTap}>
            <Ionicons name="play-outline" size={13} color="#FFD700" />
            <Text style={styles.closeBtnText}>{t('infoBanner.closeAndResume')}</Text>
          </TouchableOpacity>

        </Animated.View>
      )}

      {/* ══════════════════════ PLEIN ÉCRAN (v4) ══════════════════════ */}
      {/*
        fullscreenContent est factorisé une seule fois et réutilisé dans
        les deux chemins de rendu ci-dessous (mobile via Modal, web via
        View position:fixed) — la logique et l'apparence sont identiques,
        seul le mécanisme d'affichage change selon la plateforme.
      */}
      {fullscreenVisible && (() => {
        const fullscreenContent = (
          <View style={styles.fsCard}>

            {/* Header plein écran */}
            <View style={styles.fsHeader}>
              <View style={styles.fsHeaderLeft}>
                <View style={styles.fsIconBadge}>
                  <Ionicons name="megaphone" size={18} color="#FFD700" />
                </View>
                <View style={{ flex: 1 }}>
                  <Text style={styles.fsHeaderLabel}>{t('infoBanner.news')}</Text>
                  {infos.length > 1 && (
                    <Text style={styles.fsHeaderCounter}>
                      {currentIndex + 1} / {infos.length}
                    </Text>
                  )}
                </View>
              </View>
              {/* Bouton réduire */}
              <TouchableOpacity
                onPress={closeFullscreen}
                style={styles.fsCloseBtn}
                hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              >
                <Ionicons name="contract-outline" size={20} color="rgba(255,255,255,0.85)" />
              </TouchableOpacity>
            </View>

            {/* Contenu scrollable */}
            <View style={styles.fsBody}>
              <Text style={styles.fsTitle}>{current.titre}</Text>
              <Text style={styles.fsText}>{current.texte}</Text>
            </View>

            {/* Navigation prev / next — même logique que la carte classique */}
            {infos.length > 1 && (
              <View style={styles.fsNav}>
                <TouchableOpacity
                  style={styles.fsNavBtn}
                  onPress={() => navigateTo((currentIndex - 1 + infos.length) % infos.length)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Ionicons name="chevron-back" size={18} color="#FFD700" />
                  <Text style={styles.fsNavText}>{t('infoBanner.previous')}</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={styles.fsNavBtn}
                  onPress={() => navigateTo((currentIndex + 1) % infos.length)}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                >
                  <Text style={styles.fsNavText}>{t('infoBanner.next')}</Text>
                  <Ionicons name="chevron-forward" size={18} color="#FFD700" />
                </TouchableOpacity>
              </View>
            )}

            {/* Bouton réduire (secondaire, en bas — cohérent avec "Fermer et
                reprendre" de la carte classique) */}
            <TouchableOpacity style={styles.fsReduceBtn} onPress={closeFullscreen}>
              <Ionicons name="contract-outline" size={14} color="#FFD700" />
              <Text style={styles.fsReduceBtnText}>{t('infoBanner.reduceAndResume')}</Text>
            </TouchableOpacity>

          </View>
        );

        // ── WEB : Modal ne se rend pas de façon fiable sur certaines
        // configurations react-native-web (bug silencieux — les states
        // changent, aucune erreur, mais rien n'apparaît à l'écran). On
        // affiche donc une View en position 'fixed', mais rendue via
        // createPortal directement dans document.body plutôt que dans
        // l'arbre React normal.
        //
        // POURQUOI LE PORTAIL EST INDISPENSABLE : sans lui, même avec
        // position:'fixed' et un zIndex élevé, l'overlay reste piégé
        // DERRIÈRE le contenu si un ancêtre React (ici, la View
        // infoBannerOverlay dans games-screen.tsx) a lui-même un zIndex
        // explicite — cet ancêtre crée son propre "stacking context" CSS,
        // et un enfant en position fixed n'en sort pas automatiquement.
        // createPortal contourne le problème à la racine : le DOM produit
        // est directement attaché à document.body, complètement en dehors
        // de la hiérarchie (et donc de la pile d'empilement) de
        // games-screen.tsx — l'overlay est alors garanti de s'afficher
        // au-dessus de absolument tout le reste de la page, sans
        // exception possible.
        if (Platform.OS === 'web' && createPortal && typeof document !== 'undefined') {
          return createPortal(
            <View style={styles.fsOverlayWeb}>
              {fullscreenContent}
            </View>,
            document.body
          );
        }

        // ── MOBILE (iOS/Android) : Modal natif, comportement fiable et
        // standard — aucun souci connu, on continue de l'utiliser tel quel.
        return (
          <Modal
            visible={fullscreenVisible}
            transparent
            animationType="fade"
            onRequestClose={closeFullscreen}
            statusBarTranslucent
          >
            <View style={styles.fsOverlay}>
              {fullscreenContent}
            </View>
          </Modal>
        );
      })()}

    </View>
  );
}


// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = StyleSheet.create({

  // Conteneur racine (remplace le marginHorizontal/marginTop du wrapper v1)
  outerContainer: {
    marginHorizontal: 16,
    marginTop:        10,
  },

  // ── Bandeau ──────────────────────────────────────────────────────────────
  wrapper: {
    borderRadius: 14,
    overflow:     'hidden',
  },
  hiddenPointer: {
    opacity: 0,
  },
  inner: {
    flexDirection:     'row',
    alignItems:        'center',
    backgroundColor:   'rgba(0,0,0,0.42)',
    borderWidth:       1,
    borderColor:       'rgba(255,215,0,0.28)',
    borderRadius:      14,
    paddingVertical:   9,
    paddingHorizontal: 12,
    gap:               10,
  },

  // Pill titre
  titlePill: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               5,
    backgroundColor:   'rgba(255,215,0,0.13)',
    paddingHorizontal: 9,
    paddingVertical:   4,
    borderRadius:      8,
    flexShrink:        0,
  },
  titleText: {
    fontSize:      10,
    fontWeight:    '800',
    color:         '#FFD700',
    letterSpacing: 0.5,
  },

  // Séparateur vertical
  sep: {
    width:           1,
    height:          18,
    backgroundColor: 'rgba(255,255,255,0.20)',
  },

  // Ticker
  tickerZone: {
    flex:     1,
    overflow: 'hidden',
  },
  tickerContent: {
    alignSelf: 'flex-start',
  },
  tickerText: {
    fontSize:      12.5,
    fontWeight:    '500',
    color:         'rgba(255,255,255,0.88)',
    letterSpacing: 0.15,
  },

  // ── Carte ────────────────────────────────────────────────────────────────
  card: {
    marginTop:       6,
    backgroundColor: 'rgba(8,8,18,0.96)',
    borderWidth:     1,
    borderColor:     'rgba(255,215,0,0.28)',
    borderRadius:    14,
    padding:         16,
    gap:             14,
  },
  cardHeader: {
    flexDirection:  'row',
    alignItems:     'flex-start',
    justifyContent: 'space-between',
    gap:            8,
  },
  cardTitleRow: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           8,
    flex:          1,
  },
  cardTitle: {
    fontSize:   13,
    fontWeight: '700',
    color:      '#FFD700',
    flex:       1,
  },
  cardHeaderRight: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           10,
    flexShrink:    0,
  },
  cardCounter: {
    fontSize:      11,
    fontWeight:    '600',
    color:         'rgba(255,255,255,0.35)',
    flexShrink:    0,
  },
  // NOUVEAU v3 : bouton agrandir dans le header de la carte
  expandBtn: {
    width:           26,
    height:          26,
    borderRadius:    8,
    backgroundColor: 'rgba(255,215,0,0.12)',
    justifyContent:  'center',
    alignItems:      'center',
  },
  cardBody: {
    fontSize:   13.5,
    color:      'rgba(255,255,255,0.84)',
    lineHeight: 21,
  },

  // Navigation prev / next
  cardNav: {
    flexDirection:  'row',
    justifyContent: 'space-between',
    borderTopWidth: 1,
    borderTopColor: 'rgba(255,255,255,0.08)',
    paddingTop:     10,
  },
  navBtn: {
    flexDirection:     'row',
    alignItems:        'center',
    gap:               4,
    paddingVertical:   4,
    paddingHorizontal: 2,
  },
  navText: {
    fontSize:   12,
    fontWeight: '500',
    color:      'rgba(255,255,255,0.65)',
  },

  // Bouton fermer
  closeBtn: {
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             6,
    backgroundColor: 'rgba(255,215,0,0.10)',
    borderRadius:    9,
    paddingVertical: 9,
  },
  closeBtnText: {
    fontSize:   12,
    fontWeight: '600',
    color:      '#FFD700',
  },

  // ══════════════════════ NOUVEAU v3 : Modal plein écran ══════════════════
  fsOverlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.85)',
    justifyContent:  'center',
    alignItems:      'center',
    padding:         Platform.OS === 'web' ? 24 : 0,
  },
  // Ancrage plein viewport sur web — remplace le rôle de Modal qui ne se
  // rend pas de façon fiable sur cette plateforme (voir commentaire v4
  // en tête de fichier). 'fixed' est une valeur web-only de position,
  // acceptée par react-native-web via le cast `as any`.
  //
  // FIX v6 : `flex: 1` (hérité de fsOverlay) n'a AUCUN effet sur un
  // élément en position 'fixed' — il sort du flux normal du document,
  // donc il n'y a pas de parent flex pour lui donner une taille de
  // référence. Résultat observé : le fond sombre s'affichait bien (à
  // travers backgroundColor), mais fsCard à l'intérieur (qui a
  // height: undefined sur web, en attente d'un parent qui le contraigne)
  // se retrouvait avec une hauteur nulle et ne se rendait jamais
  // visuellement — d'où l'écran flouté vide. On redéfinit ici TOUT le
  // système de mise en page nécessaire (display/justifyContent/
  // alignItems) directement sur ce style, sans dépendre de fsOverlay.
  fsOverlayWeb: {
    position: 'fixed' as any,
    top: 0, left: 0, right: 0, bottom: 0,
    width: '100vw' as any,
    height: '100vh' as any,
    display: 'flex' as any,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.85)',
    padding: 24,
    zIndex: 9999,
  },
  fsCard: {
    width:           '100%',
    maxWidth:        560,
    height:          Platform.OS === 'web' ? undefined : '100%',
    maxHeight:       Platform.OS === 'web' ? ('84%' as any) : undefined,
    flex:            Platform.OS === 'web' ? undefined : 1,
    backgroundColor: '#0B0B18',
    borderRadius:    Platform.OS === 'web' ? 22 : 0,
    borderWidth:     Platform.OS === 'web' ? 1 : 0,
    borderColor:     'rgba(255,215,0,0.25)',
    paddingHorizontal: 22,
    paddingTop:      Platform.OS === 'web' ? 22 : 56,
    paddingBottom:   Platform.OS === 'web' ? 22 : 32,
    justifyContent:  'space-between',
  },
  fsHeader: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   18,
  },
  fsHeaderLeft: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           12,
    flex:          1,
  },
  fsIconBadge: {
    width:           38,
    height:          38,
    borderRadius:    12,
    backgroundColor: 'rgba(255,215,0,0.13)',
    borderWidth:     1,
    borderColor:     'rgba(255,215,0,0.30)',
    justifyContent:  'center',
    alignItems:      'center',
  },
  fsHeaderLabel: {
    fontSize:      11,
    fontWeight:    '800',
    color:         '#FFD700',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  fsHeaderCounter: {
    fontSize:  11,
    color:     'rgba(255,255,255,0.4)',
    marginTop: 2,
  },
  fsCloseBtn: {
    width:           36,
    height:          36,
    borderRadius:    11,
    backgroundColor: 'rgba(255,255,255,0.08)',
    justifyContent:  'center',
    alignItems:      'center',
  },
  fsBody: {
    flex: 1,
  },
  fsTitle: {
    fontSize:     20,
    fontWeight:   '800',
    color:        '#FFFFFF',
    marginBottom: 16,
    lineHeight:   26,
  },
  fsText: {
    fontSize:   15,
    color:      'rgba(255,255,255,0.82)',
    lineHeight: 24,
  },
  fsNav: {
    flexDirection:   'row',
    justifyContent:  'space-between',
    alignItems:      'center',
    borderTopWidth:  1,
    borderTopColor:  'rgba(255,255,255,0.08)',
    paddingTop:      16,
    marginTop:       16,
  },
  fsNavBtn: {
    flexDirection: 'row',
    alignItems:    'center',
    gap:           6,
    paddingVertical:   8,
    paddingHorizontal: 10,
  },
  fsNavText: {
    fontSize:   13,
    fontWeight: '700',
    color:      '#FFD700',
  },
  fsReduceBtn: {
    flexDirection:   'row',
    alignItems:      'center',
    justifyContent:  'center',
    gap:             8,
    backgroundColor: 'rgba(255,215,0,0.10)',
    borderRadius:    12,
    paddingVertical: 13,
    marginTop:       16,
  },
  fsReduceBtnText: {
    fontSize:   13,
    fontWeight: '700',
    color:      '#FFD700',
  },
});
